'use client';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { formatAmount } from '@/helpers/utils/siteUtils';
import { UpdateOrderPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import AdjustLineItems from '../../order-details/components/OrderItems/adjustLineItems';
import ProductItem from '../../order-details/components/OrderItems/productItem';
import { Button } from '../../ui/button';
import { Table, TableColumnConfig } from '../../ui/customTable/Table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { useToast } from '../../ui/use-toast';

const AdjustItemModal = ({
  open,
  setOpen,
  AdjustedItems,
  lineItems,
  Texts,
  getUpdatedData,
}: {
  open: boolean;
  setOpen: any;
  AdjustedItems: any;
  lineItems: any;
  Texts: any;
  getUpdatedData: () => void;
}) => {
  const params = useSearchParams();
  const { toast } = useToast();
  const { updateOrder } = useAccount();
  const [loading, setLoading] = useState(false);
  const [adjustItems, setAdjustItems] = useState([
    ...Array(AdjustedItems.length ?? 0).fill({
      productName: '',
      adjType: '',
      reasonDescription: '',
      unitPrice: '',
      lineSequence: '',
      itemNumber: '',
      showError: true,
      showReasonError: false,
    }),
  ]);

  const tableData = AdjustedItems?.map((item: any) => {
    const adjustedImageItem = lineItems?.find(
      (lineItem: any) => lineItem?.lineSequenceNumber === item?.LineItemInfo?.lineSequence,
    );
    return {
      lineSequence: item?.LineItemInfo?.lineSequence,
      productDetails: item?.Product?.ProductInfo?.productName,
      itemPrice: item?.ItemPriceInfo?.unitPrice,
      totalPrice: item?.ItemPriceInfo?.unitPrice * item?.LineItemInfo?.orderQuantity,
      color: item?.Product?.ProductInfo?.color,
      itemNumber: item?.Product?.ProductInfo?.legacyStyle,
      qty: item?.LineItemInfo?.orderQuantity,
      imageUrl: item?.Product?.ProductInfo?.imageUrl ? item?.Product?.ProductInfo?.imageUrl : adjustedImageItem?.image,
      pdpUrl: item?.LineItemInfo?.isShippingInsurancePolicy ? '' : item?.Product?.ProductInfo?.pdpUrl ?? '',
    };
  });

  const SetPayload = (data: any) => {
    let unitPrice = parseFloat(data?.unitPrice);
    if (data?.adjType !== Texts.itemPrice) {
      unitPrice = parseFloat(data?.previousPrice) - parseFloat(data?.unitPrice);
    }
    return {
      LineItemInfo: {
        lineSequence: data?.lineSequence,
      },
      Product: {
        ProductInfo: {
          productName: data?.productName,
          legacyStyle: data?.itemNumber,
        },
      },
      ItemPriceInfo: {
        unitPrice: unitPrice?.toFixed(2),
        previousPrice: data?.previousPrice,
        PriceAdjustments: {
          adjType: data?.adjType,
          reasonDescription: data?.reasonDescription,
        },
      },
    };
  };

  const handleAppylyAdjustments = async () => {
    const emptyAdjustments = adjustItems?.filter((items) => !items?.itemNumber);
    if (emptyAdjustments?.length === adjustItems?.length) return;
    const adjustItemsWithEmptyReason = adjustItems?.map((item) => {
      if (item?.itemNumber && !item?.reasonDescription) {
        return { ...item, showReasonError: true };
      }
      return item;
    });
    setAdjustItems(adjustItemsWithEmptyReason);
    const isError = adjustItemsWithEmptyReason?.filter(
      (items) => (items?.itemNumber && items?.showError) || (items?.itemNumber && items?.showReasonError),
    );
    if (isError?.length > 0) return;
    setLoading(true);
    const LineItems = [] as any;
    adjustItems
      ?.filter((items) => items?.itemNumber)
      ?.forEach((items) => {
        LineItems.push(SetPayload(items));
      });
    const payload = {
      query: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: {
        Recipients: [
          {
            LineItems: LineItems,
          },
        ],
      },
    } as UpdateOrderPayload;

    await updateOrder(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setOpen(false);
        getUpdatedData();
        SuccessNotification(toast, {
          description: Texts.updateLineItems,
          action: <SuccessIcon className="h-5 w-5" />,
        });
      } else {
        ErrorNotification(toast, {
          description: Texts.errorUpdatingLineItems,
          action: <ErrorIcon className="h-5 w-5" />,
        });
      }
    });
    setLoading(false);
  };

  const tableHeaders: TableColumnConfig<any>[] = [
    {
      key: 'productDetails',
      header: 'Product Details',
      render: (data, rowData) => <ProductItem Texts={Texts} productDetails={rowData} />,
    },
    {
      key: 'itemPrice',
      header: 'Item Price',
      render: (data, rowData) => {
        const adjustedItem = adjustItems?.find((items) => items?.lineSequence === rowData?.lineSequence);
        return (
          <div className="flex flex-col">
            <div
              className={`${
                adjustedItem?.unitPrice > 0 && !adjustedItem?.showError && adjustedItem?.adjType === Texts.itemPrice
                  ? 'line-through'
                  : ''
              }`}
            >{`${formatAmount(data ?? '0')}`}</div>
            {adjustedItem?.unitPrice > 0 && !adjustedItem?.showError && adjustedItem?.adjType === Texts.itemPrice && (
              <div className="font-semibold text-scarletRed">${adjustedItem?.unitPrice}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'totalPrice',
      header: 'Total Price',
      render: (data, rowData) => {
        const adjustedItem = adjustItems?.find((items) => items?.lineSequence === rowData?.lineSequence);
        return (
          <div className="flex flex-col">
            <div
              className={`${
                adjustedItem?.unitPrice > 0 && !adjustedItem?.showError && adjustedItem?.adjType !== Texts.itemPrice
                  ? 'line-through'
                  : ''
              }`}
            >{`${formatAmount(data ?? '0')}`}</div>
            {adjustedItem?.unitPrice > 0 && !adjustedItem?.showError && adjustedItem?.adjType !== Texts.itemPrice && (
              <div className="font-semibold text-scarletRed">${(data - adjustedItem?.unitPrice)?.toFixed(2)}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (data, rowData, rowIndex) => (
        <AdjustLineItems
          Texts={Texts}
          rowIndex={rowIndex}
          setAdjustItems={setAdjustItems}
          adjustItems={adjustItems}
          rowData={rowData}
        />
      ),
    },
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="custom-scrollbar max-h-[90%] max-w-[900px] overflow-y-auto bg-white sm:rounded-md">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.adjustItems}</div>
          </DialogTitle>
        </DialogHeader>
        <Table columns={tableHeaders} data={tableData} alignTextTop designTable={false} />
        <section className="flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={() => handleAppylyAdjustments()}>
            {Texts.applyAdjustment}
          </Button>
          <Button className="p-0 underline" onClick={() => setOpen(false)}>
            {Texts.cancel}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustItemModal;
